/* eslint react-hooks/exhaustive-deps: "warn" */
import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingTieOut.module.scss';

import { type DocLine, type FieldKind, type TieOutField } from './TransferPricingTieOut.types';

const DOC_LINES: DocLine[] = [
  { n: 1, caption: 'Revenue', original: 'Umsatzerlöse', amount: '41.208.000' },
  { n: 2, caption: 'Other operating income', original: 'Sonstige betriebliche Erträge', amount: '412.000' },
  { n: 5, caption: 'Cost of materials', original: 'Materialaufwand', amount: '31.731.000' },
  { n: 6, caption: 'Gross profit', original: 'Rohergebnis', amount: '9.477.000' },
  { n: 7, caption: 'Staff costs', original: 'Personalaufwand', amount: '5.912.000' },
  { n: 8, caption: 'Depreciation and amortisation', original: 'Abschreibungen', amount: '684.000' },
  { n: 9, caption: 'Other operating expenses', original: 'Sonstige betriebliche Aufwendungen', amount: '1.659.000' },
  { n: 12, caption: 'Operating result', original: 'Betriebsergebnis', amount: '1.634.000' },
];

const FIELDS: TieOutField[] = [
  {
    label: 'Turnover',
    caption: 'Umsatzerlöse',
    value: '41,208,000',
    kind: 'filed',
    lines: [1],
    note: (
      <>
        <b>Read directly from the filing.</b>
        <br />
        Line 1 · page 4 · extraction <em>OCR, reviewed</em>
        <br />
        Original caption retained: <em>Umsatzerlöse</em>
      </>
    ),
  },
  {
    label: 'Cost of materials',
    caption: 'Materialaufwand',
    value: '31,731,000',
    kind: 'filed',
    lines: [5],
    note: (
      <>
        <b>Read directly from the filing.</b>
        <br />
        Line 5 · page 4 · extraction <em>OCR, reviewed</em>
        <br />
        Original caption retained: <em>Materialaufwand</em>
      </>
    ),
  },
  {
    label: 'Gross profit',
    caption: 'Rohergebnis',
    value: '9,477,000',
    kind: 'filed',
    lines: [6],
    note: (
      <>
        <b>Read directly from the filing.</b>
        <br />
        Line 6 · page 4 · extraction <em>OCR, reviewed</em>
        <br />
        Original caption retained: <em>Rohergebnis</em>
      </>
    ),
  },
  {
    label: 'Staff costs',
    caption: 'Personalaufwand',
    value: '5,912,000',
    kind: 'filed',
    lines: [7],
    note: (
      <>
        <b>Read directly from the filing.</b>
        <br />
        Line 7 · page 4 · extraction <em>OCR, reviewed</em>
        <br />
        Original caption retained: <em>Personalaufwand</em>
      </>
    ),
  },
  {
    label: 'Operating result',
    caption: 'Betriebsergebnis',
    value: '1,634,000',
    kind: 'filed',
    lines: [12],
    note: (
      <>
        <b>Read directly from the filing.</b>
        <br />
        Line 12 · page 4 · extraction <em>OCR, reviewed</em>
        <br />
        Original caption retained: <em>Betriebsergebnis</em>
      </>
    ),
  },
  {
    label: 'Operating margin',
    caption: 'not a line in the accounts',
    value: '3.97%',
    kind: 'calc',
    lines: [12, 1],
    note: (
      <>
        <b>Calculated, not filed.</b>
        <br />
        Betriebsergebnis ÷ Umsatzerlöse
        <br />
        1.634.000 ÷ 41.208.000 = <em>3.97%</em>
        <br />
        Both inputs are filed lines, highlighted left.
      </>
    ),
  },
  {
    label: 'Employees',
    caption: 'not disclosed in this filing',
    value: '104',
    kind: 'model',
    lines: [],
    note: (
      <>
        <b>Modelled, not filed.</b>
        <br />
        This registry does not require employee numbers in an abridged submission.
        <br />
        The value is an estimate and the record says so. <em>No document is claimed for it.</em>
      </>
    ),
  },
];

const KIND_LABEL: Record<FieldKind, string> = {
  filed: 'Filed',
  calc: 'Calculated',
  model: 'Modelled',
};

const FOOT_BY_KIND: Record<FieldKind, string> = {
  filed:
    "The document stays on the record. An examiner's question about this figure has a page reference as its answer.",
  calc: 'Derived figures show their inputs. Nothing is presented as filed unless it was.',
  model: 'Where we estimate, we label it. A modelled field never carries a source document it does not have.',
};

export const TransferPricingTieOut = () => {
  const { t } = useInt();
  const [activeIndex, setActiveIndex] = useState(0);
  const field = FIELDS[activeIndex];
  const isModel = field.kind === 'model';

  return (
    <Section className={styles.section}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          className={styles.titleBlock}
          title={t('Pick a number. We will show you the page it came from.')}
          description={t(
            'This is one comparable from the set. Every field is one of three things and the record says which: read from the filing, calculated from figures that were, or modelled. Select a field to see where it resolves.',
          )}
        />
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>Exhibit D</span>
          <span className={styles.cardTitle}>Nordwaren Handels GmbH — FY2025</span>
          <span className={styles.cardTag}>illustrative</span>
        </div>

        <div className={styles.body}>
          <div className={styles.left}>
            <div className={styles.label}>Extracted record</div>

            <div className={styles.fields}>
              {FIELDS.map((item, index) => (
                <button
                  key={item.label}
                  type="button"
                  className={cn(styles.field, index === activeIndex && styles.fieldOn)}
                  onClick={() => setActiveIndex(index)}
                >
                  <span className={styles.fieldName}>
                    {item.label}
                    <i>{item.caption}</i>
                  </span>

                  <span className={styles.fieldRight}>
                    <span className={styles.fieldValue}>{item.value}</span>
                    <span className={cn(styles.pill, styles[`pill_${item.kind}`])}>{KIND_LABEL[item.kind]}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className={styles.right}>
            <div className={cn(styles.page, isModel && styles.pageDimmed)}>
              <div className={styles.pageHead}>
                <b>
                  Annual accounts to 31 December 2025 <i>· Jahresabschluss</i>
                </b>
                Handelsregister · Amtsgericht Charlottenburg · HRB 214880 B
                <br />
                Profit and loss account · page 4 · filed 28 Apr 2026
              </div>

              {DOC_LINES.map((line) => (
                <div key={line.n} className={cn(styles.docLine, field.lines.includes(line.n) && styles.docLineHit)}>
                  <span className={styles.lineNo}>{line.n}.</span>
                  <span className={styles.lineCaption}>
                    {line.caption}
                    <i>{line.original}</i>
                  </span>
                  <span className={styles.lineAmount}>{line.amount}</span>
                </div>
              ))}

              {isModel && (
                <div className={styles.modelOverlay}>
                  <span>No source document — this value is not read from a filing.</span>
                </div>
              )}
            </div>

            <div className={styles.docNote}>{field.note}</div>

            <div className={styles.foot}>{FOOT_BY_KIND[field.kind]}</div>
          </div>
        </div>
      </div>
    </Section>
  );
};
